import config from 'config/common';
import analyticsManager from 'helpers/analyticsManager';
import { ReviewedMixpanelEvents } from 'helpers/mixpanelReviewedEvents';
import { AuthFormFields } from 'store/auth/interfaces';

export type RegistrationFlowType = 'standard' | 'google';

export type EmailErrorType = 'empty' | 'invalid_format';
export type PasswordErrorType = 'empty' | 'too_short' | 'too_weak';
export type PasswordStrength = 'weak' | 'medium' | 'strong';

const MIN_PASSWORD_LENGTH = 6;
const STRONG_PASSWORD_LENGTH = 8;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const getEmailDomain = (email: string): string => {
    const atIndex = email.indexOf('@');
    if (atIndex < 0) {
        return '';
    }
    return email.slice(atIndex + 1).toLowerCase();
};

const countCharacterClasses = (password: string): number => {
    const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^a-zA-Z0-9]/];
    return classes.reduce((count, pattern) => (pattern.test(password) ? count + 1 : count), 0);
};

export const getPasswordStrength = (password: string): PasswordStrength => {
    const classes = countCharacterClasses(password);
    const isStrong = password.length >= STRONG_PASSWORD_LENGTH && classes >= 3;
    const isMedium = password.length >= MIN_PASSWORD_LENGTH && classes >= 2;
    if (isStrong) {
        return 'strong';
    }
    if (isMedium) {
        return 'medium';
    }
    return 'weak';
};

export const maskPhoneNumber = (phone: string): string => {
    if (phone.length <= 2) {
        return phone;
    }
    return '*'.repeat(phone.length - 2) + phone.slice(-2);
};

export const classifyEmailError = (value: string): EmailErrorType | null => {
    if (!value) {
        return 'empty';
    }
    if (!EMAIL_REGEX.test(value)) {
        return 'invalid_format';
    }
    return null;
};

export const classifyPasswordError = (value: string): PasswordErrorType | null => {
    if (!value) {
        return 'empty';
    }
    if (value.length < MIN_PASSWORD_LENGTH) {
        return 'too_short';
    }
    if (getPasswordStrength(value) === 'weak') {
        return 'too_weak';
    }
    return null;
};

const formatTimestamp = (ms: number): string => {
    const date = new Date(ms);
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return (
        `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}_` +
        `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    );
};

const getBrokerId = (): string => config.namespace || '';

const buildCommonProps = (flow: RegistrationFlowType, timestampMs: number) => ({
    registration_type: flow,
    broker_id: getBrokerId(),
    timestamp: formatTimestamp(timestampMs),
});

const trackRegEvent = (event: ReviewedMixpanelEvents, props: Record<string, unknown>) => {
    analyticsManager.trackEvent(event, props);
};

export const trackRegFormSuccess = (props: { countryCode?: string | null; otpRequired?: boolean }) => {
    const now = Date.now();
    trackRegEvent(ReviewedMixpanelEvents.REG_FORM_SUCCESS, {
        ...buildCommonProps('standard', now),
        country_code: props.countryCode ?? null,
        otp_required: Boolean(props.otpRequired),
        total_time_seconds: null,
    });
};

export const trackGoogleCompletionSkipped = (props: { phoneRequired: boolean; countryRequired: boolean }) => {
    const now = Date.now();
    trackRegEvent(ReviewedMixpanelEvents.REG_GOOGLE_COMPLETION_SKIPPED, {
        ...buildCommonProps('google', now),
        phone_required: props.phoneRequired,
        country_required: props.countryRequired,
    });
};

interface RegistrationTrackerOptions {
    flow: RegistrationFlowType;
    now?: () => number;
}

export class RegistrationTracker {
    private readonly flow: RegistrationFlowType;
    private readonly now: () => number;
    private readonly mountTime: number;
    private readonly focusTimes: Record<string, number> = {};
    private readonly fieldsFilled: Set<string> = new Set();
    private errorsEncountered = 0;
    private lastFieldFocused: string | null = null;
    private lastPhoneCode: string | undefined;
    private currentCountry: string | undefined;

    constructor(options: RegistrationTrackerOptions) {
        this.flow = options.flow;
        this.now = options.now || Date.now;
        this.mountTime = this.now();
    }

    private common() {
        return buildCommonProps(this.flow, this.now());
    }

    private secondsSince(startMs: number): number {
        return Math.round((this.now() - startMs) / 1000);
    }

    private timeToFill(field: string): { time_to_fill_seconds?: number } {
        const focusedAt = this.focusTimes[field];
        if (focusedAt === undefined) {
            return {};
        }
        return { time_to_fill_seconds: this.secondsSince(focusedAt) };
    }

    formViewed(props: { countryFieldEnabled?: boolean; referrer?: string } = {}) {
        trackRegEvent(ReviewedMixpanelEvents.REG_FORM_VIEWED, {
            ...this.common(),
            country_field_enabled: Boolean(props.countryFieldEnabled),
            referrer: props.referrer ?? document.referrer,
        });
    }

    fieldFocused(field: string) {
        this.focusTimes[field] = this.now();
        this.lastFieldFocused = field;
        if (field === AuthFormFields.password) {
            trackRegEvent(ReviewedMixpanelEvents.REG_PASSWORD_FOCUSED, { ...this.common() });
        }
    }

    fieldFilled(field: string, rawValue: string) {
        const value = (rawValue ?? '').trim();
        switch (field) {
            case AuthFormFields.firstName:
            case AuthFormFields.lastName:
                if (!value) {
                    return;
                }
                trackRegEvent(
                    field === AuthFormFields.firstName
                        ? ReviewedMixpanelEvents.REG_FIRST_NAME_FILLED
                        : ReviewedMixpanelEvents.REG_LAST_NAME_FILLED,
                    { ...this.common(), field_length: value.length, ...this.timeToFill(field) }
                );
                this.fieldsFilled.add(field);
                return;
            case AuthFormFields.email: {
                if (!value) {
                    return;
                }
                const emailError = classifyEmailError(value);
                if (emailError) {
                    this.errorsEncountered += 1;
                    trackRegEvent(ReviewedMixpanelEvents.REG_EMAIL_ERROR, {
                        ...this.common(),
                        error_type: emailError,
                        error_message: '',
                    });
                    return;
                }
                trackRegEvent(ReviewedMixpanelEvents.REG_EMAIL_FILLED, {
                    ...this.common(),
                    email_domain: getEmailDomain(value),
                    ...this.timeToFill(field),
                });
                this.fieldsFilled.add(field);
                return;
            }
            case AuthFormFields.password: {
                if (!value) {
                    return;
                }
                const passwordError = classifyPasswordError(value);
                if (passwordError) {
                    this.errorsEncountered += 1;
                    trackRegEvent(ReviewedMixpanelEvents.REG_PASSWORD_ERROR, {
                        ...this.common(),
                        error_type: passwordError,
                        error_message: '',
                    });
                    return;
                }
                trackRegEvent(ReviewedMixpanelEvents.REG_PASSWORD_FILLED, {
                    ...this.common(),
                    password_strength: getPasswordStrength(value),
                    ...this.timeToFill(field),
                });
                this.fieldsFilled.add(field);
                return;
            }
            case AuthFormFields.phoneNumber:
                if (!value) {
                    return;
                }
                trackRegEvent(ReviewedMixpanelEvents.REG_PHONE_NUMBER_FILLED, {
                    ...this.common(),
                    phone_country_code: this.lastPhoneCode,
                    phone_number_masked: maskPhoneNumber(value),
                    ...this.timeToFill(field),
                });
                this.fieldsFilled.add(field);
                return;
            default:
                return;
        }
    }

    phoneCodeSelected(phoneCountryCode: string, countryName?: string) {
        this.lastPhoneCode = phoneCountryCode;
        trackRegEvent(ReviewedMixpanelEvents.REG_PHONE_CODE_SELECTED, {
            ...this.common(),
            phone_country_code: phoneCountryCode,
            country_name: countryName,
        });
    }

    countrySearchStarted(query: string) {
        trackRegEvent(ReviewedMixpanelEvents.REG_COUNTRY_SEARCH_STARTED, { ...this.common(), search_query: query });
    }

    countryChange(opts: { code: string; name?: string; geoCode?: string }) {
        const { code, name, geoCode } = opts;
        const isGeoPreselect = Boolean(geoCode) && code === geoCode;

        if (this.currentCountry === undefined) {
            this.currentCountry = code;
            if (isGeoPreselect) {
                return;
            }
            trackRegEvent(ReviewedMixpanelEvents.REG_COUNTRY_SELECTED, {
                ...this.common(),
                country_code: code,
                country_name: name,
                is_geo_detected: isGeoPreselect,
            });
            this.fieldsFilled.add(AuthFormFields.countryCode);
            return;
        }

        if (code !== this.currentCountry) {
            const original = this.currentCountry;
            this.currentCountry = code;
            trackRegEvent(ReviewedMixpanelEvents.REG_COUNTRY_CHANGED, {
                ...this.common(),
                original_country_code: original,
                new_country_code: code,
            });
            this.fieldsFilled.add(AuthFormFields.countryCode);
        }
    }

    countryError(errorType: 'invalid_input' | 'empty_field', message?: string) {
        this.errorsEncountered += 1;
        trackRegEvent(ReviewedMixpanelEvents.REG_COUNTRY_ERROR, {
            ...this.common(),
            error_type: errorType,
            error_message: message ?? '',
        });
    }

    formSubmitted(props: { countryFieldEnabled?: boolean; countryWasFilled?: boolean; countryCode?: string | null }) {
        trackRegEvent(ReviewedMixpanelEvents.REG_FORM_SUBMITTED, {
            ...this.common(),
            country_field_enabled: Boolean(props.countryFieldEnabled),
            country_was_filled: Boolean(props.countryWasFilled),
            country_code: props.countryCode ?? null,
            time_to_complete_seconds: this.secondsSince(this.mountTime),
            fields_filled_count: this.fieldsFilled.size,
            errors_encountered_count: this.errorsEncountered,
        });
    }

    formAbandoned() {
        trackRegEvent(ReviewedMixpanelEvents.REG_FORM_ABANDONED, {
            ...this.common(),
            fields_filled: Array.from(this.fieldsFilled),
            last_field_focused: this.lastFieldFocused,
            time_on_page_seconds: this.secondsSince(this.mountTime),
        });
    }

    googleCompletionViewed(props: { phoneRequired: boolean; countryRequired: boolean }) {
        trackRegEvent(ReviewedMixpanelEvents.REG_GOOGLE_COMPLETION_VIEWED, {
            ...this.common(),
            phone_required: props.phoneRequired,
            country_required: props.countryRequired,
        });
    }

    googleCompletionSubmitted(props: {
        countryCode?: string | null;
        phoneCountryCode?: string;
        phoneNumber?: string;
        otpRequired?: boolean;
    }) {
        trackRegEvent(ReviewedMixpanelEvents.REG_GOOGLE_COMPLETION_SUBMITTED, {
            ...this.common(),
            country_code: props.countryCode ?? null,
            phone_country_code: props.phoneCountryCode,
            phone_number_masked: props.phoneNumber ? maskPhoneNumber(props.phoneNumber) : undefined,
            otp_required: Boolean(props.otpRequired),
            time_on_page_seconds: this.secondsSince(this.mountTime),
            fields_filled_count: this.fieldsFilled.size,
            errors_encountered_count: this.errorsEncountered,
        });
    }

    googleCompletionSuccess(props: { countryCode?: string | null; otpRequired?: boolean }) {
        trackRegEvent(ReviewedMixpanelEvents.REG_GOOGLE_COMPLETION_SUCCESS, {
            ...this.common(),
            country_code: props.countryCode ?? null,
            otp_required: Boolean(props.otpRequired),
            total_time_seconds: this.secondsSince(this.mountTime),
        });
    }

    googleCompletionAbandoned() {
        trackRegEvent(ReviewedMixpanelEvents.REG_GOOGLE_COMPLETION_ABANDONED, {
            ...this.common(),
            fields_filled: Array.from(this.fieldsFilled),
            time_on_page_seconds: this.secondsSince(this.mountTime),
        });
    }
}
